// BUILD-046 (Owner decision 2026-10-20, OWNER-APPROVED - BUILDER RECONCILIATION REQUIRED): the Priority control of the task Edit form.
//
// A task may have NO saved priority (every task assigned before priorities were saved at assignment, and any task made without one). The Edit form used
// to show such a task as "Medium" and to send `priority: 'medium'` with every save, which wrote a priority the person never chose. Now:
//   - a task with no saved priority shows an explicit first option "Not set", selected, and no priority value is assumed;
//   - a task WITH a saved priority shows it, with the options Low, Medium, High and Urgent as before (there is no "Not set" to go back to: the server has no
//     way to clear a priority, so the control does not offer one);
//   - `priority` goes into the save (PUT /work/{id}) ONLY when the person changed the control to a real value, so saving an unrelated field never writes a
//     priority silently.
// Status editing is not touched by this module.
//
// The four choices and their labels are NOT defined here: they come from priorityChoices.ts, the one list the Assign dialog also uses (Owner direction
// 2026-10-20: "use the same supported priority choices in Assign and Edit, including the existing Urgent option"). This module adds only what is specific to
// Edit: the "Not set" option for a task without a saved priority, and the rule that a priority is sent only when it changed.
//
// Pure module: no React, no I/O. Type imports, plus the pure priorityChoices module. Erasable TypeScript.

import type { WorkItemPriority } from '../../api/client';
import { PRIORITY_CHOICES, PRIORITY_LABELS, isPriorityChoice } from './priorityChoices';

/** The select's value for "no priority saved yet". */
export const PRIORITY_NOT_SET = '' as const;
export type EditPriorityChoice = WorkItemPriority | typeof PRIORITY_NOT_SET;

/** The four supported priorities, in order (the same list the Assign dialog offers). */
export const EDIT_PRIORITIES: readonly WorkItemPriority[] = PRIORITY_CHOICES;

const LABELS: Readonly<Record<WorkItemPriority, string>> = PRIORITY_LABELS;

/** A value the server can have saved: exactly one of the four known priorities. Anything else (missing, empty, unknown text) is "not set". */
function savedPriority(saved: unknown): WorkItemPriority | null {
  return isPriorityChoice(saved) ? saved : null;
}

/** What the control starts on: the saved priority, or "Not set" (never Medium) when the task has none. */
export function initialEditPriority(saved: unknown): EditPriorityChoice {
  return savedPriority(saved) ?? PRIORITY_NOT_SET;
}

/** The options in order: "Not set" first only for a task with no saved priority, then Low, Medium, High, Urgent. */
export function editPriorityOptions(saved: unknown): { value: EditPriorityChoice; label: string }[] {
  const options: { value: EditPriorityChoice; label: string }[] = EDIT_PRIORITIES.map((value) => ({ value, label: LABELS[value] }));
  return savedPriority(saved) === null ? [{ value: PRIORITY_NOT_SET, label: 'Not set' }, ...options] : options;
}

/** Reads the select's value back as a choice; anything that is not an option falls back to "Not set". */
export function parseEditPriority(value: unknown): EditPriorityChoice {
  return savedPriority(value) ?? PRIORITY_NOT_SET;
}

/**
 * The part of the save that carries the priority: `{ priority }` only when the person chose a real value that differs from what the task had, otherwise
 * nothing (so an untouched control, and "Not set" left as it was, send no priority at all).
 */
export function priorityPatch(saved: unknown, chosen: EditPriorityChoice): { priority?: WorkItemPriority } {
  const next = savedPriority(chosen);
  if (next === null) return {};
  return next === savedPriority(saved) ? {} : { priority: next };
}
